import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignSanCarlos = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Craftsman ADU Design & Construction in San Carlos",
    "description": "Custom Craftsman ADUs in San Carlos. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/craftsman-adu-design-san-carlos"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/craftsman-adu-design-san-carlos" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Craftsman ADU Design & Construction in San Carlos
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Create beautiful Craftsman ADUs in San Carlos that embody "The City of Good Living" with authentic artisan details and warm, welcoming design elements.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Craftsman ADUs in San Carlos?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Good Living Tradition</h3>
              <p className="text-gray-300">Craftsman design perfectly embodies San Carlos' spirit as "The City of Good Living," creating ADUs that enhance community warmth and hospitality.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Peninsula Charm</h3>
              <p className="text-gray-300">Authentic craftsmanship that complements San Carlos' established Peninsula character while providing modern comfort and functionality.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Community Connection</h3>
              <p className="text-gray-300">Craftsman ADUs create inviting spaces that foster the neighborhood connections and community spirit San Carlos is known for.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Traditional timber frame construction with authentic joinery</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Handcrafted built-in seating and storage features</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Welcoming front porches perfect for neighborhood interaction</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Natural stone foundations and landscape elements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Rich wood trim and authentic hardware details</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Classic divided-light windows and French door access</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            San Carlos-Specific Craftsman ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              Craftsman ADU construction in San Carlos requires understanding of community character and Peninsula development standards:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>San Carlos architectural review and neighborhood compatibility guidelines</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Historic district preservation and community character requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>San Mateo County building codes and permit procedures</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Tree preservation and mature urban canopy protection</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Peninsula transportation access and parking standards</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our Peninsula expertise ensures your Craftsman ADU enhances San Carlos' "Good Living" character while meeting all regulatory requirements.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            What to Expect: Craftsman ADU Design Process
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Community-Minded Design</h3>
              <p className="text-gray-300">We create Craftsman designs that honor San Carlos' welcoming character while providing the comfort and functionality you desire.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Thoughtful Permitting</h3>
              <p className="text-gray-300">Our team navigates San Carlos permitting with expertise in Peninsula regulations and sensitivity to community standards.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Quality Craftsmanship</h3>
              <p className="text-gray-300">Skilled artisans execute traditional Craftsman techniques to create authentic character and lasting value for your San Carlos property.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Welcoming Completion</h3>
              <p className="text-gray-300">From initial design to final walkthrough, we deliver a Craftsman ADU that embodies San Carlos' spirit of good living.</p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Cost in San Carlos
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Craftsman ADU in San Carlos?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Embrace the spirit of "The City of Good Living" with a handcrafted ADU that enhances your San Carlos property and community.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in San Carlos:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/san-carlos-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in San Carlos
              </Link>
              <Link to="/adu-cost-san-carlos" className="text-[#FF7A00] hover:underline">
                ADU Cost in San Carlos
              </Link>
              <Link to="/adu-regulations-san-carlos" className="text-[#FF7A00] hover:underline">
                ADU Regulations in San Carlos
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Craftsman ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/craftsman-adu-design-belmont" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Belmont
              </Link>
              <Link to="/craftsman-adu-design-burlingame" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Burlingame
              </Link>
              <Link to="/craftsman-adu-design-san-mateo" className="text-[#FF7A00] hover:underline">
                Craftsman ADU San Mateo
              </Link>
              <Link to="/craftsman-adu-design-redwood-city" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Redwood City
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default CraftsmanADUDesignSanCarlos;